'use client';
import dynamic from 'next/dynamic';
import React, { Suspense, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { mapRawDealerToContactInfo } from '@/app/_components/find-us-section/dealershipDateUtils';
import FindUsSection from '@/app/_components/find-us-section/FindUsSection';
import { useVehicleInfo } from '@/hooks/details-car/vehicle-info.hook';
import { useFinanceFilter } from '@/hooks/filters/finance-filter.hook';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { countVehicleView } from '@/services/CarService.service';
import { UsedCarProps } from '@/types/used-cars/used-cars.types';

import FinanceOption from '../../_components/finance-option/FinanceOption';
import CTA from '../cta/CTA';
import Gallery from '../gallery/Gallery';
import HeaderSection from '../header-section/HeaderSection';
import SafetyReport from '../safety-report/SafetyReport';
import TradeIn from '../trade-in/TradeIn';
import VehicleDetails from '../vehicle-details/VehicleDetails';
import WarrantySection from '../warranty-section/WarrantySection';

import Styles from './UsedCarPage.module.scss';

const SimilarVehicles = dynamic(
  () => import('../similar-vehicles/SimilarVehicles'),
  { ssr: false },
);

const UsedCarDetail = ({
  vehicle,
  similarVehicles,
  defaultPrice,
  dekraReports,
  cmsCallToActions,
  defaultMonthlyRepaymentValues,
}: UsedCarProps) => {
  const { setVehicleInfo } = useVehicleInfo();
  const { resetFinanceState } = useFinanceFilter();
  const { setDefaultPriceFilter } = usePriceFilter();
  const { setFormItem } = useFloatingCTA();

  useEffect(() => {
    setFormItem({
      type: 'used-car',
      usedCar: vehicle,
    });
    return () => setFormItem(undefined);
  }, [vehicle]);

  const images = vehicle.images?.map((image) => image.directus_files_id) || [];
  const { ref, inView } = useInView({
    rootMargin: '200px 0px 200px 0px',
    triggerOnce: true,
  });
  const updatePopularity = useCallback((id: string | number) => {
    const updateData = async () => {
      await countVehicleView(id);
    };

    updateData();
  }, []);

  useEffect(() => {
    setVehicleInfo(vehicle);
    setDefaultPriceFilter(defaultPrice);
    resetFinanceState();
    if (vehicle?.popularity && vehicle?.id) {
      updatePopularity(vehicle?.id);
    }
  }, []);

  return (
    <>
      <HeaderSection vehicleInfo={vehicle} />
      <Gallery vehicleInfo={vehicle} gallery={images} />
      <Suspense fallback={null}>
        <VehicleDetails vehicleInfo={vehicle} />
        {!vehicle?.reprice_monthly_repayment && (
          <FinanceOption
            vehicle={vehicle}
            defaultValues={defaultMonthlyRepaymentValues}
          />
        )}
        {dekraReports?.length ? (
          <SafetyReport dekraReport={dekraReports[0]} />
        ) : null}
        {vehicle?.dealership_id ? (
          <FindUsSection
            dealerInfo={mapRawDealerToContactInfo(vehicle?.dealership_id)}
          />
        ) : null}
        <WarrantySection warranty={vehicle?.warranty} />
        <CTA
          call_to_actions={cmsCallToActions?.vehicle_detail_call_to_actions?.slice(
            0,
            2,
          )}
        />
        <TradeIn
          footer_call_to_action={cmsCallToActions?.footer_call_to_action}
        />
        {similarVehicles && similarVehicles?.length > 0 ? (
          <div className={Styles.similar} ref={ref}>
            <Suspense fallback={null}>
              {inView ? (
                <SimilarVehicles similarVehicles={similarVehicles} />
              ) : null}
            </Suspense>
          </div>
        ) : null}
      </Suspense>
    </>
  );
};

export default UsedCarDetail;
